/**
 * Document Picker for Sage
 *
 * Allows users to reference existing case documents when chatting with Sage.
 * Supports correspondence from Case Intelligence.
 */

import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase/client';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  FileText,
  Search,
  Loader2,
  AlertTriangle,
  FileWarning,
  Check,
  FolderOpen,
  Mail,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

interface CorrespondenceDocument {
  id: string;
  title: string | null;
  correspondence_type: string;
  correspondence_date: string | null;
  from_party: string | null;
  summary: string | null;
  threat_level: string | null;
  processing_status: string;
}

interface EmailThread {
  id: string;
  subject: string | null;
  email_count: number | null;
  last_email_at: string | null;
  participants: { email: string; name: string }[] | null;
  category: string | null;
}

export interface DocumentReference {
  id: string;
  title: string;
  type: 'correspondence' | 'email_thread';
  date?: string;
  summary?: string;
}

interface DocumentPickerProps {
  onSelect: (doc: DocumentReference) => void;
  disabled?: boolean;
  selectedIds?: string[];
  compact?: boolean;
}

export function DocumentPicker({ onSelect, disabled, selectedIds = [], compact = false }: DocumentPickerProps) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [documents, setDocuments] = useState<CorrespondenceDocument[]>([]);
  const [threads, setThreads] = useState<EmailThread[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch documents and email threads when popover opens
  useEffect(() => {
    if (!open || !user?.id) return;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [corrResult, threadResult] = await Promise.all([
          supabase
            .from('case_correspondence')
            .select('id, title, correspondence_type, correspondence_date, from_party, summary, threat_level, processing_status')
            .eq('user_id', user.id)
            .eq('processing_status', 'complete')
            .order('correspondence_date', { ascending: false })
            .limit(50),
          supabase
            .from('email_threads')
            .select('id, subject, email_count, last_email_at, participants, category')
            .eq('user_id', user.id)
            .order('last_email_at', { ascending: false })
            .limit(30),
        ]);

        if (corrResult.error) throw corrResult.error;
        setDocuments(corrResult.data || []);
        setThreads(threadResult.data || []);
      } catch (err) {
        console.error('Failed to fetch documents:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [open, user?.id]);

  // Filter documents by search
  const filteredDocuments = documents.filter((doc) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      doc.title?.toLowerCase().includes(searchLower) ||
      doc.summary?.toLowerCase().includes(searchLower) ||
      doc.correspondence_type?.toLowerCase().includes(searchLower) ||
      doc.from_party?.toLowerCase().includes(searchLower)
    );
  });

  // Filter threads by search
  const filteredThreads = threads.filter((thread) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    const participantNames = (thread.participants || []).map((p) => p.name?.toLowerCase() || '').join(' ');
    return (
      thread.subject?.toLowerCase().includes(searchLower) ||
      participantNames.includes(searchLower) ||
      thread.category?.toLowerCase().includes(searchLower)
    );
  });

  const handleSelect = (doc: CorrespondenceDocument) => {
    onSelect({
      id: doc.id,
      title: doc.title || 'Untitled Document',
      type: 'correspondence',
      date: doc.correspondence_date || undefined,
      summary: doc.summary || undefined,
    });
    setOpen(false);
    setSearch('');
  };

  const handleSelectThread = (thread: EmailThread) => {
    const participantNames = (thread.participants || [])
      .map((p) => p.name || p.email)
      .filter(Boolean)
      .join(', ');
    onSelect({
      id: thread.id,
      title: thread.subject || 'Email Thread',
      type: 'email_thread',
      date: thread.last_email_at || undefined,
      summary: `${thread.email_count || 0} emails${participantNames ? ` — ${participantNames}` : ''}`,
    });
    setOpen(false);
    setSearch('');
  };

  const formatType = (type: string) => {
    return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const getThreatBadge = (level: string | null) => {
    if (!level) return null;
    const colors: Record<string, string> = {
      critical: 'bg-red-100 text-red-700 border-red-200',
      high: 'bg-orange-100 text-orange-700 border-orange-200',
      medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      low: 'bg-green-100 text-green-700 border-green-200',
      informational: 'bg-slate-100 text-slate-700 border-slate-200',
    };
    return (
      <Badge variant="outline" className={cn('text-[10px] ml-1', colors[level])}>
        {level}
      </Badge>
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className={compact ? "h-9 w-9 shrink-0" : "h-[52px] w-[52px] shrink-0"}
          disabled={disabled}
          title="Reference case documents"
        >
          <FolderOpen className={compact ? "h-4 w-4" : "h-5 w-5"} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start" side="top">
        <div className="p-3 border-b">
          <h4 className="font-medium text-sm mb-2">Reference a Document</h4>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search documents..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9"
            />
          </div>
        </div>

        <ScrollArea className="h-[300px]">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : filteredDocuments.length === 0 && filteredThreads.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileWarning className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">
                {search ? 'No documents match your search' : 'No documents uploaded yet'}
              </p>
              <p className="text-xs mt-1">
                Upload documents in Case Intelligence
              </p>
            </div>
          ) : (
            <div className="p-2 space-y-1">
              {/* Email threads section */}
              {filteredThreads.length > 0 && (
                <>
                  <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider px-2.5 pt-1 pb-0.5">
                    Email Threads
                  </p>
                  {filteredThreads.map((thread) => {
                    const isSelected = selectedIds.includes(thread.id);
                    const participantNames = (thread.participants || [])
                      .map((p) => p.name || p.email)
                      .filter(Boolean)
                      .slice(0, 3)
                      .join(', ');
                    return (
                      <button
                        key={thread.id}
                        onClick={() => handleSelectThread(thread)}
                        disabled={isSelected}
                        className={cn(
                          'w-full text-left p-2.5 rounded-md transition-colors',
                          isSelected
                            ? 'bg-navy-50 cursor-not-allowed opacity-60'
                            : 'hover:bg-slate-100'
                        )}
                      >
                        <div className="flex items-start gap-2">
                          <Mail className="h-4 w-4 mt-0.5 text-indigo-500 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1">
                              <span className="font-medium text-sm truncate">
                                {thread.subject || 'No Subject'}
                              </span>
                              {isSelected && <Check className="h-3.5 w-3.5 text-navy-600" />}
                              <Badge variant="outline" className="text-[10px] ml-1 bg-indigo-50 text-indigo-700 border-indigo-200">
                                {thread.email_count || 0} emails
                              </Badge>
                            </div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                              {participantNames && <span>{participantNames}</span>}
                              {thread.last_email_at && (
                                <span className={participantNames ? ' ml-1' : ''}>
                                  {participantNames ? '• ' : ''}{format(new Date(thread.last_email_at), 'MMM d, yyyy')}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </>
              )}

              {/* Correspondence section */}
              {filteredDocuments.length > 0 && filteredThreads.length > 0 && (
                <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider px-2.5 pt-2 pb-0.5">
                  Correspondence
                </p>
              )}
              {filteredDocuments.map((doc) => {
                const isSelected = selectedIds.includes(doc.id);
                return (
                  <button
                    key={doc.id}
                    onClick={() => handleSelect(doc)}
                    disabled={isSelected}
                    className={cn(
                      'w-full text-left p-2.5 rounded-md transition-colors',
                      isSelected
                        ? 'bg-navy-50 cursor-not-allowed opacity-60'
                        : 'hover:bg-slate-100'
                    )}
                  >
                    <div className="flex items-start gap-2">
                      <FileText className="h-4 w-4 mt-0.5 text-navy-500 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1">
                          <span className="font-medium text-sm truncate">
                            {doc.title || 'Untitled'}
                          </span>
                          {isSelected && <Check className="h-3.5 w-3.5 text-navy-600" />}
                          {getThreatBadge(doc.threat_level)}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {formatType(doc.correspondence_type)}
                          {doc.correspondence_date && (
                            <span className="ml-1">
                              • {format(new Date(doc.correspondence_date), 'MMM d, yyyy')}
                            </span>
                          )}
                        </div>
                        {doc.summary && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                            {doc.summary}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>

        <div className="p-2 border-t bg-slate-50 text-xs text-muted-foreground text-center">
          Sage will analyze the document in context
        </div>
      </PopoverContent>
    </Popover>
  );
}
